
import { AlertTriangle, Clock, FileSearch, Shield, FileClock, Users } from "lucide-react";
import { LandingPageTemplate } from "@/components/layout/LandingPageTemplate";
import { HeroSection } from "@/components/landing/HeroSection";
import { FeatureSection } from "@/components/landing/FeatureSection";
import { ProcessSection } from "@/components/landing/ProcessSection";
import { BenefitSection } from "@/components/landing/BenefitSection";
import { CtaSection } from "@/components/landing/CtaSection";

const IncidentResponse = () => {
  return (
    <LandingPageTemplate
      title="Incident Response Planning"
      description="Develop a comprehensive incident response strategy to minimize the impact of security breaches and ensure business continuity."
      keywords={[
        "incident response planning", 
        "cybersecurity incident", 
        "breach response", 
        "security incident", 
        "cyber response plan", 
        "incident management"
      ]}
      canonicalUrl="/cybersecurity/incident-response-planning/"
      schemaData={{
        name: "Incident Response Planning Service",
        description: "Comprehensive incident response planning and preparation to minimize the impact of security incidents on your business.",
        provider: {
          name: "edgefi",
          sameAs: "https://edgefi.com"
        }
      }}
    >
      <HeroSection
        title="Effective"
        highlighted="Incident Response Planning"
        description="Prepare your organization to respond quickly and effectively to security incidents with a comprehensive incident response plan tailored to your business."
        ctaText="Schedule a Consultation"
        secondaryButtonText="Learn More"
        secondaryButtonLink="#features"
        modalTitle="Incident Response Consultation"
        modalDescription="Tell us about your security incident response needs and challenges, and we'll help you develop an effective plan."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "Incident Response Planning",
          service_interest: "Security Planning"
        }}
        heroPattern="grid"
      />

      <FeatureSection 
        id="features"
        title="Comprehensive Incident Response Planning"
        subtitle="SECURITY PREPAREDNESS"
        description="Our incident response planning service ensures your organization is prepared to handle security incidents effectively."
        background="light"
        features={[
          {
            title: "Response Strategy",
            description: "Development of a tailored incident response strategy aligned with your business requirements and risk profile.",
            icon: <Shield className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Documentation & Playbooks",
            description: "Creation of detailed incident response plans and playbooks for common incident scenarios.",
            icon: <FileSearch className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Team Preparation",
            description: "Definition of incident response team roles, responsibilities, and communication protocols.",
            icon: <Users className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Incident Simulation",
            description: "Tabletop exercises and simulations to test and refine your incident response capabilities.",
            icon: <AlertTriangle className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Recovery Planning",
            description: "Business continuity and disaster recovery integration to ensure operational resilience.",
            icon: <FileClock className="h-6 w-6 text-purple-600" />
          },
          {
            title: "Response Time Optimization",
            description: "Process improvements to minimize detection, response, and recovery timeframes.",
            icon: <Clock className="h-6 w-6 text-purple-600" />
          }
        ]}
      />

      <ProcessSection 
        title="Our Incident Response Planning Process"
        subtitle="METHODOLOGY"
        description="A structured approach to developing and implementing an effective incident response capability."
        steps={[
          {
            title: "Risk Assessment",
            description: "Identify critical assets, potential threats, and the most likely incident scenarios for your organization."
          },
          {
            title: "Plan Development",
            description: "Create comprehensive incident response plans, playbooks, and documentation tailored to your needs."
          },
          {
            title: "Team Organization",
            description: "Define roles, responsibilities, and communication channels for effective incident management."
          },
          {
            title: "Resource Preparation",
            description: "Ensure necessary tools, technologies, and external resources are in place and ready for response."
          },
          {
            title: "Testing & Simulation",
            description: "Conduct tabletop exercises and simulations to validate and refine response capabilities."
          },
          {
            title: "Continuous Improvement",
            description: "Regularly review and update plans based on lessons learned, new threats, and business changes."
          }
        ]}
        layout="vertical"
      />

      <BenefitSection 
        title="Benefits of Incident Response Planning"
        subtitle="BUSINESS VALUE"
        background="gradient"
        benefits={[
          {
            title: "Faster Response",
            description: "Minimize damage by reducing the time between incident detection and effective response."
          },
          {
            title: "Lower Impact",
            description: "Reduce the financial, operational, and reputational impact of security incidents."
          },
          {
            title: "Regulatory Compliance",
            description: "Meet incident response requirements in various regulatory frameworks and standards."
          },
          {
            title: "Stakeholder Confidence",
            description: "Demonstrate preparedness to customers, partners, and regulators with documented response plans."
          },
          {
            title: "Operational Resilience",
            description: "Maintain business operations during and after security incidents with minimal disruption."
          },
          {
            title: "Team Readiness",
            description: "Ensure response team members understand their roles and can execute them effectively under pressure."
          }
        ]}
      />

      <CtaSection 
        title="Ready to Strengthen Your Incident Response Capability?"
        description="Schedule a consultation to discuss your incident response needs and develop a plan that protects your business."
        ctaText="Schedule Your Consultation"
        modalTitle="Incident Response Planning Consultation"
        modalDescription="Let's discuss your organization's specific needs and develop an incident response plan that works for your business."
        additionalFields={{
          lead_source: "Website",
          lead_interest: "Incident Response Planning",
          service_interest: "Security Planning"
        }}
      />
    </LandingPageTemplate>
  );
};

export default IncidentResponse;
